'use client'

import type { DocumentContent, UserDocument } from '@/types/documents'
import type { LyricsResult, ReaderContent } from '@/types/reader'
import { buildDictionaryLookupFromTexts } from './dictionary-resolver'
import {
  documentContentToReaderContent,
  documentTextToBlocks,
  plainDocumentContentToReaderContent
} from './document-adapter'
import { lyricsResultToReaderContent } from './lyrics-adapter'
import { buildGlossaryFromTokens } from './glossary'

export async function processDocumentToReaderContent(
  document: Pick<
    UserDocument,
    'id' | 'title' | 'file_name' | 'file_type' | 'created_at' | 'updated_at'
  > & { content: DocumentContent },
  options: {
    isOffline?: boolean
    onProgress?: (progress: { completed: number; total: number }) => void
  } = {}
) {
  const draft = documentContentToReaderContent(document)
  const lookup = await buildDictionaryLookupFromTexts(
    draft.blocks.map((block) => block.originalText),
    options
  )
  return documentContentToReaderContent(document, lookup)
}

export async function processPlainTextToReaderContent(
  id: string,
  title: string,
  content: DocumentContent,
  options: {
    isOffline?: boolean
    onProgress?: (progress: { completed: number; total: number }) => void
  } = {}
) {
  const draft = plainDocumentContentToReaderContent(id, title, content)
  const lookup = await buildDictionaryLookupFromTexts(
    draft.blocks.map((block) => block.originalText),
    options
  )
  return plainDocumentContentToReaderContent(id, title, content, lookup)
}

export async function processLyricsToReaderContent(
  input: {
    id: string
    title: string
    subtitle?: string
    videoId?: string
    artist?: string
    duration?: number
    lyricsOffset?: number
    result: LyricsResult
  },
  options: {
    isOffline?: boolean
    maxWordLength?: number
    onProgress?: (progress: { completed: number; total: number }) => void
  } = {}
): Promise<ReaderContent> {
  const draft = lyricsResultToReaderContent(input)
  const lookup = await buildDictionaryLookupFromTexts(
    draft.blocks.map((block) => block.originalText),
    { ...options, maxWordLength: options.maxWordLength ?? 4 }
  )
  return lyricsResultToReaderContent(input, lookup)
}

export function retokenizeBlocksWithLookup(
  content: ReaderContent,
  lookup: Parameters<typeof documentTextToBlocks>[1]
): ReaderContent {
  const blocks = content.blocks.map((block) => ({
    ...block,
    tokens: documentTextToBlocks(block.originalText, lookup)[0]?.tokens ?? []
  }))
  const entriesById = new Map(
    [...(lookup?.values() ?? [])]
      .flat()
      .map((entry) => [entry.id, entry] as const)
  )

  return {
    ...content,
    blocks,
    glossary: buildGlossaryFromTokens(
      blocks.flatMap((block) => block.tokens),
      entriesById
    )
  }
}
